import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';

function SavedComparisonView({ comparison, onClose }) {
  const [activeTab, setActiveTab] = useState('comparison');
  const [downloading, setDownloading] = useState(false);
  
  const isInvalidSOF = comparison?.status === 'Invalid SOF';
  const subscription = comparison?.subscription_data?.subscription || {};
  const customer = comparison?.subscription_data?.customer || {};
  const plan = comparison?.subscription_data?.plan || {};

  const getFieldColor = (sofValue, chargebeeValue) => {
    return sofValue.toLowerCase() === chargebeeValue.toLowerCase() ? 'text-green-600' : 'text-red-600';
  };

  const handleDownloadSOF = async () => {
    setDownloading(true);
    try {
      const userHash = localStorage.getItem('userHash');
      const response = await axiosInstance.get(`/api/download-sof/${comparison.id}`, {
        headers: {
          Authorization: userHash
        },
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', comparison.sof_id + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading SOF:', error);
      if (error.response?.status === 404) {
        alert('The PDF file for this comparison was not found. This may be an older comparison before file storage was implemented.');
      } else {
        alert('Failed to download SOF: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setDownloading(false);
    }
  };

  const logComparisonValues = () => {
    if (!comparison) return;
    
    if (isInvalidSOF) {
      console.log('=== SOF TO CHARGEBEE COMPARISON LOG ===');
      console.log('No matching subscriptions found for comparison (Invalid SOF)');
      console.log('========================================');
      return;
    }

    console.log('=== SOF TO CHARGEBEE COMPARISON LOG ===');
    
    // Customer comparison
    const sofCustomer = comparison.sof_data?.billing_contact_information?.customer || '';
    const chargebeeCustomer = customer.company || '';
    console.log(`Customer - SOF: "${sofCustomer}" | Chargebee: "${chargebeeCustomer}"`);
    
    // Email comparison
    const sofEmail = comparison.sof_data?.billing_contact_information?.email_address || '';
    const chargebeeEmail = customer.email || '';
    console.log(`Email - SOF: "${sofEmail}" | Chargebee: "${chargebeeEmail}"`);
    
    // Total Amount comparison
    const sofAmount = comparison.sof_data?.product_information?.total_net_price || '';
    const chargebeeAmountFormatted = `$${getSubscriptionAmount().toFixed(2)}`;
    console.log(`Total Amount - SOF: "${sofAmount}" | Chargebee: "${chargebeeAmountFormatted}"`);
    
    // Product Instance Domain comparison
    const sofProductDomain = comparison.sof_data?.product_information?.product_instance_domain || '';
    const chargebeeProductDomain = subscription.cf_values?.product_instance_domain || 
                                 subscription.cf_product_instance_domain || 
                                 subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                                 '';
    
    // Also check alternative locations for domain (same logic as ComparisonModal)
    let alternativeChargebeeDomain = '';
    for (const key in subscription) {
      if (key.includes('product_instance_domain') || key.includes('domain')) {
        if (subscription[key] && !alternativeChargebeeDomain) {
          alternativeChargebeeDomain = subscription[key];
        }
      }
    }
    
    const finalChargebeeDomain = chargebeeProductDomain || alternativeChargebeeDomain || subscription.id || '';
    const domainMatch = (finalChargebeeDomain && sofProductDomain && sofProductDomain.includes(finalChargebeeDomain));
    console.log(`Product Instance Domain - SOF: "${sofProductDomain}" | Chargebee: "${finalChargebeeDomain}" | Match: ${domainMatch}`);
    
    console.log('========================================');
  };

  useEffect(() => {
    logComparisonValues();
  }, [comparison]);

  if (!comparison) return null;

  const getSubscriptionAmount = () => {
    console.log('=== FRONTEND SUBSCRIPTION DEBUG ===');
    console.log('Full subscription object:', subscription);
    console.log('MRR field:', subscription.mrr);
    console.log('Subscription items:', subscription.subscription_items);
    console.log('Plan amount:', subscription.plan_amount);
    console.log('Calculated total amount dollars:', subscription.calculated_total_amount_dollars);
    console.log('All subscription fields:', Object.keys(subscription));
    
    // First priority: Use MRR (Monthly Recurring Revenue) field - matches /subscriptions logic
    if (subscription.mrr !== undefined && subscription.mrr > 0) {
      console.log('Using MRR field:', subscription.mrr);
      return subscription.mrr / 100; // Convert cents to dollars
    }
    
    // Second priority: Check if backend has already calculated the total amount
    if (subscription.calculated_total_amount_dollars !== undefined) {
      console.log('Using calculated_total_amount_dollars:', subscription.calculated_total_amount_dollars);
      return subscription.calculated_total_amount_dollars;
    }
    
    // Third priority: Check if subscription has subscription_items array
    if (subscription.subscription_items && Array.isArray(subscription.subscription_items)) {
      console.log('Processing subscription_items array...');
      // Sum up all amounts from subscription items
      const totalCents = subscription.subscription_items.reduce((total, item) => {
        console.log('Item:', item, 'Amount:', item.amount);
        return total + (item.amount || 0);
      }, 0);
      console.log('Total cents from items:', totalCents);
      console.log('Total dollars from items:', totalCents / 100);
      return totalCents / 100; // Convert cents to dollars
    }
    
    // Fourth priority: Fallback to other possible field names
    const amount = subscription.plan_amount || 
                   subscription.amount || 
                   subscription.plan_unit_price || 
                   subscription.unit_price ||
                   (plan.price && plan.price) ||
                   (plan.unit_price && plan.unit_price) ||
                   0;
    console.log('Fallback amount (cents):', amount);
    console.log('Fallback amount (dollars):', amount / 100);
    console.log('=== END FRONTEND SUBSCRIPTION DEBUG ===');
    return amount / 100; // Convert cents to dollars
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-screen w-screen z-50 flex items-start justify-center" style={{ margin: 0, padding: 0 }}>
      <div className="relative mt-10 p-5 border w-11/12 max-w-[1400px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Saved SOF Comparison - {comparison.sof_id}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">SOF ID</p>
                <p className="font-medium">{comparison.sof_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Upload Date</p>
                <p className="font-medium">{new Date(comparison.upload_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  comparison.status === 'Compliant' 
                    ? 'bg-green-100 text-green-800' 
                    : comparison.status === 'Invalid SOF'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {comparison.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Errors</p>
                <p className="font-medium text-red-600">{comparison.errors === -1 ? 'INVALID' : comparison.errors}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Signature</p>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  comparison.signature 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {comparison.signature ? 'Yes' : 'No'}
                </span>
              </div>
              {comparison.signature && comparison.signature_date && (
                <div>
                  <p className="text-sm text-gray-600">Signature Date</p>
                  <p className="font-medium">{new Date(comparison.signature_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Tab buttons for Compliant/Needs Review cases */}
          {!isInvalidSOF && (
            <div className="flex space-x-2 mb-4">
              <button
                onClick={() => setActiveTab('comparison')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'comparison'
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                SOF Comparison with Chargebee
              </button>
              <button
                onClick={() => setActiveTab('extracted')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'extracted'
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Extracted Content from SOF
              </button>
              <button
                onClick={handleDownloadSOF}
                disabled={downloading}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  downloading
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {downloading ? 'Downloading...' : 'Download Uploaded SOF'}
              </button>
            </div>
          )}

          {isInvalidSOF ? (
            // Show SOF data for Invalid SOF cases
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Extracted SOF Information (No Chargebee Match Found)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Contact Information</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Customer:</span> {comparison.sof_data.billing_contact_information?.customer || 'N/A'}</p>
                    <p><span className="font-medium">Contact Name:</span> {comparison.sof_data.billing_contact_information?.contact_name || 'N/A'}</p>
                    <p><span className="font-medium">Email:</span> {comparison.sof_data.billing_contact_information?.email_address || 'N/A'}</p>
                    <p><span className="font-medium">Phone:</span> {comparison.sof_data.billing_contact_information?.phone || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Product Information</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Product Instance ID:</span> {comparison.sof_data.product_information?.product_instance_id || 'N/A'}</p>
                    <p><span className="font-medium">Product Instance Domain:</span> {comparison.sof_data.product_information?.product_instance_domain || 'N/A'}</p>
                    <p><span className="font-medium">Item Name:</span> {comparison.sof_data.product_information?.item_name || 'N/A'}</p>
                    <p><span className="font-medium">Total Net Price:</span> {comparison.sof_data.product_information?.total_net_price || 'N/A'}</p>
                    <p><span className="font-medium">Quantity:</span> {comparison.sof_data.product_information?.quantity || 'N/A'}</p>
                    <p><span className="font-medium">Duration:</span> {comparison.sof_data.product_information?.duration_months || 'N/A'} months</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Subscription Details</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Start Date:</span> {comparison.sof_data.subscription_details?.subscription_term_start_date || 'N/A'}</p>
                    <p><span className="font-medium">End Date:</span> {comparison.sof_data.subscription_details?.subscription_term_end_date || 'N/A'}</p>
                    <p><span className="font-medium">Billing Frequency:</span> {comparison.sof_data.subscription_details?.billing_frequency || 'N/A'}</p>
                    <p><span className="font-medium">Payment Method:</span> {comparison.sof_data.subscription_details?.payment_method || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Billing Address</h5>
                  <div className="space-y-1 text-sm">
                    <p className="whitespace-pre-wrap">{comparison.sof_data.billing_contact_information?.bill_to_address || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Show Chargebee data for valid matches
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Customer Details</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Company:</span> {customer.company || 'N/A'}</p>
                  <p><span className="font-medium">Email:</span> {customer.email || 'N/A'}</p>
                  <p><span className="font-medium">Name:</span> {customer.first_name} {customer.last_name}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Subscription Details</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Status:</span> 
                    <span className={`ml-1 px-2 py-1 rounded text-xs ${
                      subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                      subscription.status === 'in_trial' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status}
                    </span>
                  </p>
                  <p><span className="font-medium">Customer ID:</span> {plan.name || subscription.customer_id}</p>
                  <p><span className="font-medium">Amount:</span> 
                    ${getSubscriptionAmount().toFixed(2)} 
                    {subscription.currency_code} / {subscription.billing_period_unit}
                  </p>
                  <p><span className="font-medium">Quantity:</span> {subscription.plan_quantity || 1}</p>
                  <p><span className="font-medium">Term End:</span> 
                    {subscription.current_term_end ? 
                      new Date(subscription.current_term_end * 1000).toLocaleDateString() : 
                      'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isInvalidSOF && activeTab === 'comparison' && (
            <div className="mt-6 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Comparison with SOF</h4>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 text-sm">
                <div className="bg-gray-100 border-b border-r border-gray-300 p-3 font-semibold text-gray-700">
                  Field
                </div>
                <div className="bg-gray-100 border-b border-r border-gray-300 p-3 font-semibold text-gray-700">
                  SOF Value
                </div>
                <div className="bg-gray-100 border-b border-gray-300 p-3 font-semibold text-gray-700">
                  Chargebee Value
                </div>
                
                <div className="border-b border-r border-gray-300 p-3 bg-white font-medium">Customer</div>
                <div className={`border-b border-r border-gray-300 p-3 bg-white ${
                  getFieldColor(
                    comparison.sof_data.billing_contact_information?.customer || '',
                    customer.company || ''
                  )
                }`}>
                  {comparison.sof_data.billing_contact_information?.customer || 'N/A'}
                </div>
                <div className="border-b border-gray-300 p-3 bg-white font-bold">{customer.company || 'N/A'}</div>
                
                <div className="border-b border-r border-gray-300 p-3 bg-gray-50 font-medium">Email</div>
                <div className={`border-b border-r border-gray-300 p-3 bg-gray-50 ${
                  getFieldColor(
                    comparison.sof_data.billing_contact_information?.email_address || '',
                    customer.email || ''
                  )
                }`}>
                  {comparison.sof_data.billing_contact_information?.email_address || 'N/A'}
                </div>
                <div className="border-b border-gray-300 p-3 bg-gray-50 font-bold">{customer.email || 'N/A'}</div>
                
                <div className="border-b border-r border-gray-300 p-3 bg-white font-medium">Total Amount</div>
                <div className={`border-b border-r border-gray-300 p-3 bg-white ${
                  (() => {
                    const sofAmount = parseFloat((comparison.sof_data.product_information?.total_net_price || '0').replace(/[^0-9.-]+/g, ''));
                    const chargebeeAmount = getSubscriptionAmount();
                    return sofAmount === chargebeeAmount ? 'text-green-600' : 'text-red-600';
                  })()
                }`}>
                  {comparison.sof_data.product_information?.total_net_price || 'N/A'}
                </div>
                <div className="border-b border-gray-300 p-3 bg-white font-bold">
                  ${getSubscriptionAmount().toFixed(2)}
                </div>
                
                <div className="border-r border-gray-300 p-3 bg-gray-50 font-medium">Product Instance Domain</div>
                <div className={`border-r border-gray-300 p-3 bg-gray-50 ${
                  (() => {
                    const sofProductDomain = comparison.sof_data.product_information?.product_instance_domain || '';
                    const chargebeeProductDomain = subscription.cf_values?.product_instance_domain || 
                                                 subscription.cf_product_instance_domain || 
                                                 subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                                                 '';
                    
                    // Also check alternative locations for domain (same logic as ComparisonModal)
                    let alternativeChargebeeDomain = '';
                    for (const key in subscription) {
                      if (key.includes('product_instance_domain') || key.includes('domain')) {
                        if (subscription[key] && !alternativeChargebeeDomain) {
                          alternativeChargebeeDomain = subscription[key];
                        }
                      }
                    }
                    
                    const finalChargebeeDomain = chargebeeProductDomain || alternativeChargebeeDomain || subscription.id || '';
                    
                    // Consider it a match if SOF domain contains or equals the Chargebee domain, or both are empty (same logic as ComparisonModal)
                    const match = (!finalChargebeeDomain && !sofProductDomain) || 
                                (finalChargebeeDomain && sofProductDomain && 
                                 (sofProductDomain === finalChargebeeDomain || sofProductDomain.includes(finalChargebeeDomain)));
                    return match ? 'text-green-600' : 'text-red-600';
                  })()
                }`}>
                  {comparison.sof_data.product_information?.product_instance_domain || 'N/A'}
                </div>
                <div className="p-3 bg-gray-50 font-bold">
                  {(() => {
                    // DEBUG: Log the subscription object to see what's available
                    console.log('=== DOMAIN DEBUG ===');
                    console.log('Full subscription object:', subscription);
                    console.log('cf_values:', subscription.cf_values);
                    console.log('cf_product_instance_domain:', subscription.cf_product_instance_domain);
                    console.log('subscription.id:', subscription.id);
                    
                    // FIXED: Display subscription ID instead of domain for Product Instance Domain field
                    // This ensures we show the Chargebee subscription ID in the Product Instance Domain column
                    if (subscription.id) {
                      console.log('Using subscription ID for Product Instance Domain:', subscription.id);
                      return subscription.id;
                    }
                    
                    console.log('=== END DOMAIN DEBUG ===');
                    return 'N/A';
                  })()}
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Extracted Content Tab for Compliant/Needs Review cases */}
          {!isInvalidSOF && activeTab === 'extracted' && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Extracted SOF Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Contact Information</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Customer:</span> {comparison.sof_data.billing_contact_information?.customer || 'N/A'}</p>
                    <p><span className="font-medium">Contact Name:</span> {comparison.sof_data.billing_contact_information?.contact_name || 'N/A'}</p>
                    <p><span className="font-medium">Email:</span> {comparison.sof_data.billing_contact_information?.email_address || 'N/A'}</p>
                    <p><span className="font-medium">Phone:</span> {comparison.sof_data.billing_contact_information?.phone || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Product Information</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Product Instance ID:</span> {comparison.sof_data.product_information?.product_instance_id || 'N/A'}</p>
                    <p><span className="font-medium">Product Instance Domain:</span> {comparison.sof_data.product_information?.product_instance_domain || 'N/A'}</p>
                    <p><span className="font-medium">Item Name:</span> {comparison.sof_data.product_information?.item_name || 'N/A'}</p>
                    <p><span className="font-medium">Total Net Price:</span> {comparison.sof_data.product_information?.total_net_price || 'N/A'}</p>
                    <p><span className="font-medium">Quantity:</span> {comparison.sof_data.product_information?.quantity || 'N/A'}</p>
                    <p><span className="font-medium">Duration:</span> {comparison.sof_data.product_information?.duration_months || 'N/A'} months</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Subscription Details</h5>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Start Date:</span> {comparison.sof_data.subscription_details?.subscription_term_start_date || 'N/A'}</p>
                    <p><span className="font-medium">End Date:</span> {comparison.sof_data.subscription_details?.subscription_term_end_date || 'N/A'}</p>
                    <p><span className="font-medium">Billing Frequency:</span> {comparison.sof_data.subscription_details?.billing_frequency || 'N/A'}</p>
                    <p><span className="font-medium">Payment Method:</span> {comparison.sof_data.subscription_details?.payment_method || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Billing Address</h5>
                  <div className="space-y-1 text-sm">
                    <p className="whitespace-pre-wrap">{comparison.sof_data.billing_contact_information?.bill_to_address || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

         
        </div>
      </div>
    </div>
  );
}

export default SavedComparisonView;